import { knolToFSRSCard } from "@core/fsrs/scheduler";
import { isDeckFSRS } from "@models/deckSettings";
import { User } from "@models/user";
import Globals from "globals";
import { getGlobalTotalReviews } from "hooks/data/responseHistory";
import { superCache } from "hooks/data/superCache";
import { ID } from "types/ID";

declare let NativeNotifications: any;

export const defaultStudyGoal = 10;
export const studyGoalMax = 10000;

const localStorageKey = "AnkiApp.settings.studyGoal";

// Debounce badge updates to avoid excessive calls
let badgeUpdateTimeout: NodeJS.Timeout | null = null;

export function getStudyGoal(): number {
  return Number.parseInt(localStorage[localStorageKey], 10) || defaultStudyGoal;
}

function setBadgeCount(count: number): Promise<number> {
  const goal = getStudyGoal();

  return new Promise((resolve, reject) => {
    NativeNotifications.setBadgeCount(count, goal, resolve, reject);
  });
}

async function remainingReviews(): Promise<number> {
  const goal = getStudyGoal();

  const count = await getGlobalTotalReviews();
  if (count >= goal) {
    return 0;
  }

  return goal - count;
}

async function getDueCardCount(): Promise<number> {
  // Check if superCache is initialized
  if (!superCache.initialized) {
    return await remainingReviews();
  }

  // Get due cards from FSRS decks
  if (!superCache.decks || !superCache.knols) {
    return 0;
  }

  const now = Date.now();

  // Find FSRS decks and calculate due count directly
  const fsrsDeckIDs = new Set<ID>();
  for (const [id, deck] of superCache.decks.entries()) {
    if (isDeckFSRS(deck)) {
      fsrsDeckIDs.add(id);
    }
  }

  if (fsrsDeckIDs.size === 0) {
    return await remainingReviews();
  }

  // Count due cards in FSRS decks directly
  let dueCount = 0;
  const todayCutoff = now;

  for (const [, knol] of superCache.knols.entries()) {
    if (fsrsDeckIDs.has(knol.deck_id)) {
      try {
        const fsrsCard = knolToFSRSCard(knol, now);
        if (fsrsCard && fsrsCard.state !== "New" && fsrsCard.due <= todayCutoff) {
          dueCount++;
        }
      } catch (error) {
        // Silently ignore conversion errors
      }
    }
  }

  return dueCount;
}

/**
 * Internal function that actually updates the badge count
 */
async function doUpdateBadgeCount(): Promise<void> {
  if (Globals.platform !== "ios") {
    return;
  }

  try {
    if (User.isLoggedIn()) {
      const dueCards = await getDueCardCount();
      await setBadgeCount(dueCards);
    } else {
      await setBadgeCount(0);
    }
  } catch (err) {
    // Silently handle badge update errors
  }
}

/**
 * Updates the iOS app badge count to show the number of due cards.
 *
 * For FSRS decks: Shows the count of cards that are due for review now.
 * For non-FSRS decks: Falls back to showing remaining study goal progress.
 *
 * The badge count is calculated by:
 * 1. Finding all decks with FSRS enabled
 * 2. Converting knols in those decks to FSRS cards
 * 3. Counting cards that are due now (excluding new cards)
 * 4. If no FSRS decks exist, showing study goal progress instead
 *
 * This function is automatically called when:
 * - App initializes or comes to foreground
 * - Cards are reviewed (responses updated)
 * - Deck/knol data changes
 * - Study goal settings change
 *
 * Updates are debounced to avoid excessive calls.
 */
export async function updateBadgeCount(): Promise<void> {
  if (badgeUpdateTimeout) {
    clearTimeout(badgeUpdateTimeout);
  }

  badgeUpdateTimeout = setTimeout(() => {
    doUpdateBadgeCount();
    badgeUpdateTimeout = null;
  }, 100); // 100ms debounce
}

// Backward compatibility alias
export const updateStudyGoalBadgeCount = updateBadgeCount;
